import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Upload, Trash2, LogOut, Image, Video,
  Eye, EyeOff, Plus, Home, Mail, LayoutDashboard,
  MessageSquare, Settings, BarChart3, Users, Check,
  Star, Save, ExternalLink, FileText
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import MessageDetailModal from "@/components/MessageDetailModal";
import OffersTab from "@/components/admin/OffersTab";
import useMessageNotifications from "@/hooks/useMessageNotifications";
import { Bell } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboardPage() {
  const [user, setUser] = useState(null);
  const [media, setMedia] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({});
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [testimonialDialogOpen, setTestimonialDialogOpen] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({ name: "", location: "", text: "" });
  const [selectedContact, setSelectedContact] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const navigate = useNavigate();

  const openContactDetail = (contact) => {
    setSelectedContact(contact);
    setDetailModalOpen(true);
    if (!contact.read) markContactRead(contact.id);
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: getAuthHeaders() });
      if (res.data.role !== "admin") throw new Error("Not admin");
      setUser(res.data);
    } catch {
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    }
  }, [getAuthHeaders, navigate]);

  const fetchAll = useCallback(async () => {
    try {
      const [mediaRes, contactsRes, statsRes, testimonialsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/media`, { headers: getAuthHeaders() }),
        axios.get(`${API}/contacts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/testimonials`, { headers: getAuthHeaders() }),
        axios.get(`${API}/settings`, { headers: getAuthHeaders() }),
      ]);
      setMedia(mediaRes.data);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
      setTestimonials(testimonialsRes.data);
      setSettings(settingsRes.data);
    } catch { /* ignore partial failures */ }
  }, [getAuthHeaders]);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  // Poll for new messages every 30s and trigger notifications (browser + toast + sound)
  const fetchContactsOnly = useCallback(async () => {
    if (!user) return [];
    try {
      const res = await axios.get(`${API}/contacts`, { headers: getAuthHeaders() });
      // Sync into local state so the badge count stays live
      setContacts(res.data);
      setStats((prev) => ({ ...prev, unread_contacts: res.data.filter((c) => !c.read).length }));
      return res.data;
    } catch { return []; }
  }, [user, getAuthHeaders]);

  const { permission: notifPermission, requestPermission } = useMessageNotifications({
    fetchContacts: fetchContactsOnly,
    enabled: !!user,
    intervalMs: 30000,
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    const fileInput = e.target.querySelector('input[type="file"]');
    const titleInput = e.target.querySelector('input[name="title"]');
    const file = fileInput?.files?.[0];
    if (!file) { toast.error("Selectionnez un fichier"); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error("Max 100MB"); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", titleInput?.value || "");
    formData.append("order", media.length);
    try {
      await axios.post(`${API}/media/upload`, formData, { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success("Media ajoute");
      fetchAll();
      setUploadDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    setUploading(false);
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm("Supprimer ce media?")) return;
    try { await axios.delete(`${API}/media/${id}`, { headers: getAuthHeaders() }); toast.success("Supprime"); fetchAll(); }
    catch { toast.error("Erreur"); }
  };

  const toggleMediaActive = async (item) => {
    try { await axios.put(`${API}/media/${item.id}`, { active: !item.active }, { headers: getAuthHeaders() }); fetchAll(); }
    catch { toast.error("Erreur"); }
  };

  const markContactRead = async (id) => {
    try { await axios.put(`${API}/contacts/${id}/read`, {}, { headers: getAuthHeaders() }); fetchAll(); }
    catch { /* ignore */ }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Supprimer ce message?")) return;
    try { await axios.delete(`${API}/contacts/${id}`, { headers: getAuthHeaders() }); toast.success("Supprime"); fetchAll(); }
    catch { toast.error("Erreur"); }
  };

  const handleAddTestimonial = async () => {
    if (!newTestimonial.name || !newTestimonial.text) { toast.error("Nom et texte requis"); return; }
    try {
      await axios.post(`${API}/testimonials`, { ...newTestimonial, order: testimonials.length }, { headers: getAuthHeaders() });
      toast.success("Temoignage ajoute");
      setNewTestimonial({ name: "", location: "", text: "" });
      setTestimonialDialogOpen(false);
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Supprimer?")) return;
    try { await axios.delete(`${API}/testimonials/${id}`, { headers: getAuthHeaders() }); fetchAll(); }
    catch { toast.error("Erreur"); }
  };

  const saveSettings = async () => {
    try { await axios.put(`${API}/settings`, settings, { headers: getAuthHeaders() }); toast.success("Parametres sauvegardes"); }
    catch { toast.error("Erreur"); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch {}
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  if (!user) return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center"><div className="text-sm text-[#475569]">Chargement...</div></div>;

  const TABS = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "media", label: "Media Hero", icon: Image },
    { id: "contacts", label: "Messages", icon: Mail, badge: stats.unread_contacts },
    { id: "offers", label: "Offres PDF", icon: FileText },
    { id: "testimonials", label: "Temoignages", icon: Star },
    { id: "settings", label: "Parametres", icon: Settings },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1B2B5B] text-white flex flex-col shrink-0 min-h-screen">
        <div className="p-6 border-b border-white/10">
          <span className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
            <span className="text-[#00C853]">Aya</span>Pay
          </span>
          <span className="text-xs text-slate-400 ml-2">Admin</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
              {tab.badge > 0 && (
                <span className="ml-auto bg-[#00C853] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{tab.badge}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          {/* Notifications permission button */}
          {notifPermission !== "granted" ? (
            <button
              onClick={async () => {
                const r = await requestPermission();
                if (r === "granted") toast.success("Notifications activees");
                else if (r === "denied") toast.error("Notifications bloquees dans le navigateur");
              }}
              data-testid="enable-notifications-btn"
              className="w-full flex items-center gap-2 text-xs text-[#00C853] hover:text-white bg-[#00C853]/10 hover:bg-[#00C853]/20 px-3 py-2 rounded-lg transition-colors"
            >
              <Bell size={14} /> Activer les notifications
            </button>
          ) : (
            <div className="flex items-center gap-2 text-xs text-slate-400 px-3 py-2" data-testid="notifications-enabled">
              <Bell size={14} className="text-[#00C853]" /> Notifications ON
            </div>
          )}
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white px-4 py-2">
            <ExternalLink size={14} /> Voir le site
          </a>
          <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 px-4 py-2 w-full">
            <LogOut size={14} /> Deconnexion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Dashboard tab */}
        {activeTab === "dashboard" && (
          <div>
            <h1 className="text-2xl font-bold text-[#0A1128] mb-8">Tableau de bord</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {[
                { label: "Messages", value: stats.total_contacts || 0, sub: `${stats.unread_contacts || 0} non lus`, icon: Mail, color: "bg-blue-50 text-blue-600" },
                { label: "Media Hero", value: stats.total_media || 0, sub: `${stats.active_media || 0} actifs`, icon: Image, color: "bg-green-50 text-green-600" },
                { label: "Temoignages", value: stats.total_testimonials || 0, sub: "publies", icon: Star, color: "bg-amber-50 text-amber-600" },
                { label: "Admin", value: user?.email || "", sub: "connecte", icon: Users, color: "bg-purple-50 text-purple-600" },
              ].map((card, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-[#475569]">{card.label}</span>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${card.color}`}>
                      <card.icon size={16} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-[#0A1128]">{card.value}</div>
                  <div className="text-xs text-[#475569] mt-1">{card.sub}</div>
                </div>
              ))}
            </div>

            {/* Recent messages */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-[#0A1128] mb-4">Derniers messages</h2>
              {contacts.length === 0 ? (
                <p className="text-sm text-[#475569]">Aucun message</p>
              ) : (
                <div className="space-y-3">
                  {contacts.slice(0, 5).map(c => (
                    <button
                      key={c.id}
                      onClick={() => openContactDetail(c)}
                      data-testid={`dashboard-message-${c.id}`}
                      className={`w-full text-left flex items-start justify-between p-3 rounded-lg transition-colors ${c.read ? "bg-white hover:bg-slate-50" : "bg-blue-50/50 hover:bg-blue-50"}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#0A1128]">{c.name} {!c.read && <span className="inline-block w-2 h-2 bg-[#00C853] rounded-full ml-1"></span>}</div>
                        <div className="text-xs text-[#475569]">{c.email}</div>
                        <div className="text-xs text-[#475569] mt-1 line-clamp-1">{c.message}</div>
                      </div>
                      <span className="text-xs text-[#475569] shrink-0 ml-3">{new Date(c.created_at).toLocaleDateString("fr-CH")}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Media tab */}
        {activeTab === "media" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-[#0A1128]">Media du carrousel</h1>
                <p className="text-sm text-[#475569] mt-1">Videos et images du hero (page d'accueil)</p>
              </div>
              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="upload-media-btn" className="bg-[#00C853] hover:bg-[#00A844] text-white rounded-lg text-sm">
                    <Plus size={16} className="mr-1.5" /> Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader><DialogTitle>Ajouter un media</DialogTitle></DialogHeader>
                  <form onSubmit={handleUpload} className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Fichier (Video ou Image, max 100MB)</label>
                      <Input type="file" accept="video/mp4,video/webm,video/mov,image/jpeg,image/png,image/webp,image/gif" data-testid="media-file-input" className="rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Titre (optionnel)</label>
                      <Input name="title" placeholder="Titre du media" className="rounded-lg" />
                    </div>
                    <Button type="submit" disabled={uploading} data-testid="upload-submit" className="w-full bg-[#1B2B5B] hover:bg-[#0f1d3d] text-white rounded-lg">
                      {uploading ? "Upload en cours..." : "Uploader"} <Upload size={16} className="ml-2" />
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {media.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Image size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-[#475569]">Aucun media. Les images par defaut seront affichees.</p>
                <p className="text-xs text-[#475569] mt-1">Ajoutez des videos ou images pour personnaliser votre hero.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Apercu</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Titre</TableHead>
                      <TableHead>Fichier</TableHead>
                      <TableHead className="w-20">Actif</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {media.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          {item.type === "image" ? (
                            <img src={item.url.startsWith("http") ? item.url : `${BACKEND}${item.url}`} alt="" loading="lazy" className="w-12 h-12 object-cover rounded" />
                          ) : (
                            <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center"><Video size={16} className="text-slate-400" /></div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${item.type === "video" ? "bg-blue-50 text-blue-700" : "bg-green-50 text-green-700"}`}>
                            {item.type === "video" ? "Video" : "Image"}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-[#0A1128]">{item.title || "-"}</TableCell>
                        <TableCell className="text-sm text-[#475569] truncate max-w-[200px]">{item.filename}</TableCell>
                        <TableCell>
                          <button onClick={() => toggleMediaActive(item)} className={`p-1.5 rounded ${item.active ? "text-[#00C853]" : "text-slate-300"}`}>
                            {item.active ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                        </TableCell>
                        <TableCell>
                          <button onClick={() => handleDeleteMedia(item.id)} className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50">
                            <Trash2 size={16} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {/* Contacts tab */}
        {activeTab === "contacts" && (
          <div>
            <h1 className="text-2xl font-bold text-[#0A1128] mb-6">Messages de contact</h1>
            {contacts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Mail size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-[#475569]">Aucun message</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} data-testid={`contact-card-${c.id}`} className={`bg-white rounded-xl border p-6 ${c.read ? "border-slate-200" : "border-[#00C853]/30 bg-[#00C853]/5"}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm text-[#0A1128] flex items-center gap-2">
                          {c.name}
                          {!c.read && <span className="text-xs bg-[#00C853] text-white px-2 py-0.5 rounded-full">Nouveau</span>}
                        </div>
                        <div className="text-xs text-[#475569] mt-0.5 break-words">{c.email} {c.phone && `| ${c.phone}`} {c.company && `| ${c.company}`}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-3">
                        <span className="text-xs text-[#475569] mr-2">{new Date(c.created_at).toLocaleDateString("fr-CH")}</span>
                        <button onClick={() => openContactDetail(c)} data-testid={`view-contact-${c.id}`} className="p-1.5 text-[#1B2B5B] hover:bg-slate-100 rounded" title="Voir tout le détail">
                          <Eye size={14} />
                        </button>
                        {!c.read && (
                          <button onClick={() => markContactRead(c.id)} className="p-1.5 text-[#00C853] hover:bg-[#00C853]/10 rounded" title="Marquer comme lu">
                            <Check size={14} />
                          </button>
                        )}
                        <button onClick={() => deleteContact(c.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded" title="Supprimer">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-[#475569] leading-relaxed whitespace-pre-wrap break-words">{c.message}</p>
                    <div className="flex flex-wrap gap-3 mt-3">
                      {c.email && (
                        <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-xs text-[#1B2B5B] hover:text-[#00C853] font-medium">
                          <Mail size={12} /> Répondre par email
                        </a>
                      )}
                      <button onClick={() => openContactDetail(c)} className="inline-flex items-center gap-1 text-xs text-[#00C853] hover:text-[#00A844] font-medium">
                        <Eye size={12} /> Voir détail complet
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Testimonials tab */}
        {activeTab === "testimonials" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-[#0A1128]">Temoignages clients</h1>
                <p className="text-sm text-[#475569] mt-1">Gerez les avis affiches sur le site</p>
              </div>
              <Dialog open={testimonialDialogOpen} onOpenChange={setTestimonialDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#00C853] hover:bg-[#00A844] text-white rounded-lg text-sm">
                    <Plus size={16} className="mr-1.5" /> Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader><DialogTitle>Ajouter un temoignage</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Nom du client / commerce *</label>
                      <Input value={newTestimonial.name} onChange={e => setNewTestimonial(p => ({...p, name: e.target.value}))} placeholder="Restaurant XYZ" className="rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Ville</label>
                      <Input value={newTestimonial.location} onChange={e => setNewTestimonial(p => ({...p, location: e.target.value}))} placeholder="Geneve" className="rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Temoignage *</label>
                      <Textarea value={newTestimonial.text} onChange={e => setNewTestimonial(p => ({...p, text: e.target.value}))} placeholder="Leur avis..." rows={4} className="rounded-lg resize-none" />
                    </div>
                    <Button onClick={handleAddTestimonial} className="w-full bg-[#1B2B5B] hover:bg-[#0f1d3d] text-white rounded-lg">
                      Ajouter <Plus size={16} className="ml-2" />
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {testimonials.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Star size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-[#475569]">Aucun temoignage personnalise. Les temoignages par defaut sont affiches.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {testimonials.map(t => (
                  <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-medium text-sm text-[#0A1128]">{t.name}</div>
                        <div className="text-xs text-[#475569]">{t.location}</div>
                      </div>
                      <button onClick={() => deleteTestimonial(t.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <p className="text-sm text-[#475569] italic">"{t.text}"</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Offers tab */}
        {activeTab === "offers" && (
          <OffersTab settings={settings} />
        )}

        {/* Settings tab */}
        {activeTab === "settings" && (
          <div>
            <h1 className="text-2xl font-bold text-[#0A1128] mb-6">Parametres du site</h1>
            <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-2xl">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Telephone</label>
                  <Input value={settings.phone || ""} onChange={e => setSettings(p => ({...p, phone: e.target.value}))} className="rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Email</label>
                  <Input value={settings.email || ""} onChange={e => setSettings(p => ({...p, email: e.target.value}))} className="rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Adresse</label>
                  <Input value={settings.address || ""} onChange={e => setSettings(p => ({...p, address: e.target.value}))} className="rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Titre hero personnalise (laissez vide pour le defaut)</label>
                  <Input value={settings.hero_title || ""} onChange={e => setSettings(p => ({...p, hero_title: e.target.value}))} placeholder="Votre terminal de paiement" className="rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1128] mb-1.5">Sous-titre hero personnalise</label>
                  <Textarea value={settings.hero_subtitle || ""} onChange={e => setSettings(p => ({...p, hero_subtitle: e.target.value}))} placeholder="Description sous le titre..." rows={3} className="rounded-lg resize-none" />
                </div>
                <Button onClick={saveSettings} className="bg-[#00C853] hover:bg-[#00A844] text-white rounded-lg">
                  <Save size={16} className="mr-2" /> Sauvegarder les parametres
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Message detail modal — accessible from any tab */}
      <MessageDetailModal
        contact={selectedContact}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />
    </div>
  );
}
